import {
  createSlice,
  createAsyncThunk,
  createSelector } from
'@reduxjs/toolkit';
import { ApolloClient, gql, NormalizedCacheObject } from '@apollo/client';
import { RootState } from '@src/redux/store';
import * as TYPES from '@src/redux/types';

const GRAPHQL = gql`
  mutation createKitDevice($data: createKitDeviceInput!) {
    createKitDevice(data: $data) {
      id
      status
      error {
        message
        statusCode
      }
    }
  }
`;

const initialState: TYPES.MutationResponseState = {
  result: null,
  error: null,
  loading: false
};

export const createKitDeviceRequest = createAsyncThunk<
  TYPES.MutationResponse,
  {data: TYPES.CreateKitDeviceInput;},
  {extra: ApolloClient<NormalizedCacheObject>;}>(

  'kit_device/create_kit_device',
  async (params: {data: TYPES.CreateKitDeviceInput;}, { extra: client }) => {
    try {
      const response = await client.mutate({
        mutation: GRAPHQL,
        variables: params
      });
      return response.data.createKitDevice;
    } catch (error) {
      const err = error as {message?: String;statusCode?: String;};
      return Promise.reject({
        message: err.message || 'An error occurred',
        statusCode: err.statusCode || '500'
      });
    }
  }
);

const createKitDeviceSlice = createSlice({
  name: 'create_kit_device',
  initialState: initialState,
  reducers: {
    createKitDeviceClear: (state) => {
      state.result = null;
      state.error = null;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder.
    addCase(createKitDeviceRequest.pending, (state) => {
      state.result = null;
      state.error = null;
      state.loading = true;
    }).
    addCase(createKitDeviceRequest.fulfilled, (state, action) => {
      state.result = action.payload;
      state.loading = false;
    }).
    addCase(createKitDeviceRequest.rejected, (state, action) => {
      state.error = action.payload as Error || {
        message: 'Failed to fetch',
        statusCode: '500'
      };
      state.loading = false;
    });
  }
});

export const { createKitDeviceClear } = createKitDeviceSlice.actions;

export const createKitDevice = createSelector(
  (state: RootState) => state.kitDevice.createKitDevice.result,
  (state: RootState) => state.kitDevice.createKitDevice.error,
  (state: RootState) => state.kitDevice.createKitDevice.loading,
  (result, error, loading) => ({
    result,
    error,
    loading
  })
);

export default createKitDeviceSlice.reducer;